import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Map } from 'immutable';
import VisibilitySensor from 'react-visibility-sensor';
import moment from 'moment-timezone';
import get from 'lodash/get';

import * as AppContext from 'lib/app-context';
import { CHECKOUT_AUTO_EXTEND_LABEL_IMPRESSION, CHECKOUT_AUTO_EXTEND_LABEL_CLICK, CHECKOUT_AUTO_EXTEND_TOOLTIP_IMPRESSION, CHECKOUT_AUTO_EXTEND_TOOLTIP_CLICK, ADD_TIME_BUTTON_CLICK, ADD_TIME_BUTTON_IMPRESSION, TIME_FORMAT } from 'lib/analytics/events';

import { Location } from 'models/locations';
import Quote from 'models/quote';
import Venue from 'models/venue';
import Search from 'models/search';
import Pricing, { EVENT_PRICING } from 'models/pricing';

import CheckoutTimePicker from 'components/checkout/parking-details/time-picker';
import MonthlyParkingStartDate from 'components/checkout/parking-details/monthly-parking-start-date';
import EventParkingPeriod from 'components/checkout/parking-details/event-parking-period';
import EventPackageEvents from 'components/checkout/parking-details/event-package-events';

import changeMonthlyStart from 'action-creators/checkout/change-monthly-start';
import toggleBookExtendedTimesCreator from 'action-creators/checkout/toggle-book-extended-times';
import trackEventCreator from 'action-creators/analytics/track-event';
import setModalState from 'action-creators/modal/set-modal-state';
import dismissTimePickerPrompt from 'action-creators/search/dismiss-time-picker-prompt';
import changeTimes from 'action-creators/search/change-times';

const propTypes = {
  timezone: PropTypes.string,
  bookExtendedTimes: PropTypes.bool.isRequired,
  sellers: PropTypes.instanceOf(Map),
  selectedLocation: PropTypes.instanceOf(Location).isRequired,
  selectedQuote: PropTypes.instanceOf(Quote),
  monthlyStart: PropTypes.instanceOf(moment),
  parkingMinStart: PropTypes.instanceOf(moment),
  msa: PropTypes.string.isRequired,
  disclaimers: PropTypes.string,
  afterEventEnd: PropTypes.number,
  trackEvent: PropTypes.func.isRequired,
  changeMonthlyStart: PropTypes.func.isRequired,
  toggleBookExtendedTimes: PropTypes.func.isRequired,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  error: PropTypes.instanceOf(Map).isRequired,
  venue: PropTypes.instanceOf(Venue),
  changeModalState: PropTypes.func.isRequired,
  appContext: PropTypes.string.isRequired,
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  timePickerPromptDismissed: PropTypes.bool.isRequired,
  saveSearch: PropTypes.func.isRequired,
  dismissTimePickerPrompt: PropTypes.func.isRequired,
  changeTimes: PropTypes.func.isRequired,
  directAffiliateTraffic: PropTypes.bool.isRequired,
  pricing: PropTypes.instanceOf(Pricing),
  locale: PropTypes.string.isRequired,
  onLocationDetailsVisibilityChange: PropTypes.func.isRequired,
};

const defaultProps = {
  disclaimers: null,
  afterEventEnd: null,
  monthlyStart: moment(),
  parkingMinStart: null,
  startTime: '',
  endTime: '',
  selectedLocation: {},
  msa: '',
  venue: null,
  sellers: Map(),
  selectedQuote: new Quote(),
  timezone: moment.tz.guess(),
  pricing: null,
};


class ParkingDetails extends Component {
  constructor(props) {
    super(props);

    this.handleToggleBookExtendedTimes = this.handleToggleBookExtendedTimes.bind(this);
    this.handleMonthlyStartChange = this.handleMonthlyStartChange.bind(this);
    this.toggleCalendar = this.toggleCalendar.bind(this);
    this.hideCalendar = this.hideCalendar.bind(this);
    this.toggleAutoExtendTooltip = this.toggleAutoExtendTooltip.bind(this);
    this.hideAutoExtendTooltip = this.hideAutoExtendTooltip.bind(this);
    this.startDateChange = this.startDateChange.bind(this);
    this.startTimeChange = this.startTimeChange.bind(this);
    this.endDateChange = this.endDateChange.bind(this);
    this.endTimeChange = this.endTimeChange.bind(this);
    this.showMobileTimePicker = this.showMobileTimePicker.bind(this);
    this.onEditTimeVisibilityChange = this.onEditTimeVisibilityChange.bind(this);
    this.trackEditTimeEvent = this.trackEditTimeEvent.bind(this);
  }

  componentWillMount() {
    const { selectedQuote, timezone } = this.props;
    const { startTime, endTime } = selectedQuote;

    this.setState({
      displayStartTime: moment(startTime).tz(timezone),
      displayEndTime: moment(endTime).tz(timezone),
      initialized: false,
      showCalendar: false,
      showAutoExtendTooltip: false,
    });
  }

  componentDidMount() {
    if (this.shouldRenderAutoExtendLabel) {
      this.props.trackEvent({
        ...CHECKOUT_AUTO_EXTEND_LABEL_IMPRESSION,
        properties: {
          end_time: this.props.currentSearch.endTime.format(TIME_FORMAT),
        },
      });
    }
  }

  trackEditTimeEvent(event) {
    this.props.trackEvent({
      ...event,
      properties: {
        label: this.editTimeLabel,
      },
    });
  }

  onEditTimeVisibilityChange(visible) {
    if (visible && this.props.appContext !== AppContext.DESKTOP) {
      this.trackEditTimeEvent(ADD_TIME_BUTTON_IMPRESSION);
    }
  }

  handleChangeTime() {
    window.history.back();
  }

  handleMonthlyStartChange(e) {
    const newMonthlyStart = e;
    this.setState({ showCalendar: false });
    this.props.changeMonthlyStart({ monthlyStart: newMonthlyStart || moment() });
  }

  handleToggleBookExtendedTimes(e) {
    e.preventDefault();
    e.stopPropagation();
    const { bookExtendedTimes, toggleBookExtendedTimes, trackEvent } = this.props;

    toggleBookExtendedTimes();
    this.hideAutoExtendTooltip(e);
    trackEvent({
      ...CHECKOUT_AUTO_EXTEND_TOOLTIP_CLICK,
      properties: {
        'Book Extended Times': bookExtendedTimes ? '1' : '0',
      },
    });
  }

  renderDisclaimers() {
    if (this.props.disclaimers) {
      return (
        <div>
          <div dangerouslySetInnerHTML={{ __html: this.props.disclaimers.replace(/\n/g, '<br />') }} />
        </div>
      );
    }
    return null;
  }

  toggleAutoExtendTooltip(e) {
    e.stopPropagation();
    e.preventDefault();

    this.setState({
      showAutoExtendTooltip: !this.state.showAutoExtendTooltip,
    }, () => {
      this.props.trackEvent({
        ...CHECKOUT_AUTO_EXTEND_LABEL_CLICK,
        properties: {
          display: this.state.showAutoExtendTooltip ? '1' : '0',
        },
      });

      if (this.state.showAutoExtendTooltip) {
        this.props.trackEvent(CHECKOUT_AUTO_EXTEND_TOOLTIP_IMPRESSION);
      }

      this.props.dismissTimePickerPrompt();
    });
  }

  hideAutoExtendTooltip(e) {
    this.props.dismissTimePickerPrompt();
    e.stopPropagation();

    this.props.trackEvent({
      ...CHECKOUT_AUTO_EXTEND_LABEL_CLICK,
      properties: {
        display: '0',
      },
    });

    this.setState({ showAutoExtendTooltip: false });
  }

  get shouldRenderAutoExtendLabel() {
    const { startTime, minStartTime, endTime, maxEndTime } = this.props.selectedQuote;
    const { timezone, venue, directAffiliateTraffic } = this.props;
    if (directAffiliateTraffic) { return false; }

    return !(venue && venue.enhancedAirport) &&
    (!startTime.tz(timezone).isSame(minStartTime.tz(timezone)) ||
    !endTime.tz(timezone).isSame(maxEndTime.tz(timezone)));
  }

  startDateChange(value) {
    this.props.saveSearch();
    const date = moment(this.props.selectedQuote.startTime);
    value.hour(date.hours());
    value.minute(date.minutes());
    this.props.dismissTimePickerPrompt();
    this.props.changeTimes({ startTime: value });
  }

  startTimeChange(input) {
    this.props.saveSearch();
    const date = moment(this.props.selectedQuote.startTime);
    date.hour(input.hour);
    date.minute(input.minute);
    this.props.dismissTimePickerPrompt();
    this.props.changeTimes({ startTime: date });
  }

  endDateChange(value) {
    const { bookExtendedTimes, selectedQuote } = this.props;
    this.props.saveSearch();
    const date = moment(bookExtendedTimes ? selectedQuote.maxEndTime : selectedQuote.endTime);
    value.hour(date.hours());
    value.minute(date.minutes());
    this.props.dismissTimePickerPrompt();
    this.props.changeTimes({ endTime: value });
  }

  endTimeChange(value) {
    const { bookExtendedTimes, selectedQuote } = this.props;
    this.props.saveSearch();
    const date = moment(bookExtendedTimes ? selectedQuote.maxEndTime : selectedQuote.endTime);
    date.hour(value.hour);
    date.minute(value.minute);
    this.props.dismissTimePickerPrompt();
    this.props.changeTimes({ endTime: date });
  }

  get editTimeLabel() {
    const { pricing } = this.props;
    return get(pricing, 'type', null) === EVENT_PRICING ? 'ADD TIME' : 'EDIT TIME';
  }

  showMobileTimePicker() {
    const { appContext, changeModalState } = this.props;
    if (appContext !== 'mobile') { return; }

    window.scrollTo(0, 0);
    changeModalState({
      displayModal: true,
      header: 'searchPickerHeader',
      headerProps: {
        monthlyAvailable: false,
        displayMonthly: false,
      },
      body: 'mobileDestinationTimePicker',
      bodyProps: {
        displayAutocomplete: false,
        variation: 'checkout',
        quoteTimesChange: this.props.changeTimes,
      },
      footer: 'buttonFooter',
      footerProps: {
        footerButtonText: 'FIND PARKING',
      },
    });
    this.trackEditTimeEvent(ADD_TIME_BUTTON_CLICK);
  }

  toggleCalendar(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ showCalendar: !this.state.showCalendar });
  }

  hideCalendar(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ showCalendar: false });
  }

  render() {
    const { selectedQuote: quote } = this.props;
    const { quoteType } = quote;

    return (
      <div>
        <VisibilitySensor onChange={this.props.onLocationDetailsVisibilityChange} partialVisibility>
          <Fragment>
            <div className="checkout-section">
              <CheckoutTimePicker
                shouldRenderAutoExtendLabel={this.shouldRenderAutoExtendLabel}
                quote={quote}
                sellers={this.props.sellers}
                editTimeLabel={this.editTimeLabel}
                timezone={this.props.timezone}
                trackEvent={this.props.trackEvent}
                currentSearch={this.props.currentSearch}
                selectedQuote={quote}
                selectedLocation={this.props.selectedLocation}
                error={this.props.error}
                bookExtendedTimes={this.props.bookExtendedTimes}
                startTimeChange={this.startTimeChange}
                startDateChange={this.startDateChange}
                endTimeChange={this.endTimeChange}
                endDateChange={this.endDateChange}
                showMobileTimePicker={this.showMobileTimePicker}
                onEditTimeVisibilityChange={this.onEditTimeVisibilityChange}
                directAffiliateTraffic={this.props.directAffiliateTraffic}
                toggleBookExtendedTimes={this.props.toggleBookExtendedTimes}
                toggleAutoExtendTooltip={this.toggleAutoExtendTooltip}
                showAutoExtendTooltip={this.state.showAutoExtendTooltip}
                hideAutoExtendTooltip={this.hideAutoExtendTooltip}
                locale={this.props.locale}
                appContext={this.props.appContext}
                timePickerPromptDismissed={this.props.timePickerPromptDismissed}
                dismissTimePickerPrompt={this.props.dismissTimePickerPrompt}
              />
              <EventParkingPeriod
                quoteType={quoteType}
                parkingMinStart={this.props.parkingMinStart}
                afterEventEnd={this.props.afterEventEnd}
                timezone={this.props.timezone}
                shouldRenderAutoExtendLabel={this.shouldRenderAutoExtendLabel}
                directAffiliateTraffic={this.props.directAffiliateTraffic}
                bookExtendedTimes={this.props.bookExtendedTimes}
                quote={quote}
                selectedLocation={this.props.selectedLocation}
                sellers={this.props.sellers}
                toggleBookExtendedTimes={this.props.toggleBookExtendedTimes}
                toggleAutoExtendTooltip={this.toggleAutoExtendTooltip}
                appContext={this.props.appContext}
                showAutoExtendTooltip={this.state.showAutoExtendTooltip}
                hideAutoExtendTooltip={this.hideAutoExtendTooltip}
              />
              <MonthlyParkingStartDate
                quote={quote}
                selectedLocation={this.props.selectedLocation}
                monthlyStart={this.props.monthlyStart}
                hideCalendar={this.hideCalendar}
                handleMonthlyStartChange={this.handleMonthlyStartChange}
                locale={this.props.locale}
                showCalendar={this.state.showCalendar}
                toggleCalendar={this.toggleCalendar}
                msa={this.props.msa}
              />
              <EventPackageEvents quote={quote} />
            </div>
          </Fragment>
        </VisibilitySensor>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const {
    afterEventEnd,
    events,
    packagePricings,
    bookExtendedTimes,
    monthlyStart,
    pricing,
  } = state.checkout;

  const {
    currentSearch,
    timePickerPromptDismissed,
    venue,
    sellers,
  } = state.search;

  const {
    insights,
  } = state.analytics;

  const {
    appContext,
    directAffiliateTraffic,
  } = state.app;

  return {
    afterEventEnd,
    events,
    packagePricings,
    bookExtendedTimes,
    monthlyStart,
    insights,
    currentSearch,
    timePickerPromptDismissed,
    venue,
    sellers,
    appContext,
    directAffiliateTraffic,
    pricing,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    changeMonthlyStart,
    toggleBookExtendedTimes: toggleBookExtendedTimesCreator,
    trackEvent: trackEventCreator,
    changeModalState: setModalState,
    dismissTimePickerPrompt,
    changeTimes,
  }, dispatch)
);

ParkingDetails.propTypes = propTypes;
ParkingDetails.defaultProps = defaultProps;

export default connect(mapStateToProps, mapDispatchToProps)(ParkingDetails);
